<template>
  <div :class="['base-checkbox', { 'invalid': !valid }]">
    <div class="base-checkbox__input-wrapper">
      <input
        :id="name"
        class="base-checkbox__input"
        type="checkbox"
        :name="name"
        :checked="value"
        :disabled="disabled"
        @keyup.enter="$emit('click')"
        @click="$emit('click')"
        @blur="$emit('blur')"
        @change="$emit('change', $event.target.checked)"
      >
      <label
        :for="name"
        :class="['base-checkbox__label', {'required': required}]"
      >
        <i v-if="value" class="icon-checkmark base-checkbox__icon" />
        <slot />
      </label>
    </div>
    <div class="base-checkbox__error-message">
      <transition name="fade">
        <slot name="error-message" v-bind="{ errorMessage }">
          <div
            :id="`${nameWithoutWhitespace}-error`"
            :class="{ 'display-none': valid }"
            aria-live="assertive"
          >
            {{ errorMessage }}
          </div>
        </slot>
      </transition>
    </div>
  </div>
</template>

<script>
import { computed } from '@nuxtjs/composition-api'

export default {
  name: 'BaseCheckbox',
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    name: {
      type: String,
      required: true
    },
    value: {
      type: Boolean,
      required: true
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    valid: {
      type: Boolean,
      default: true
    },
    errorMessage: {
      type: String,
      default: ''
    }
  },
  setup (props) {
    const nameWithoutWhitespace = computed(() => props.name.replace(/\s/g, ''))

    return {
      nameWithoutWhitespace
    }
  }
}
</script>

<style lang="scss">
.base-checkbox {
  $this: &;

  position: relative;
  display: flex;
  align-items: center;
  padding-left: var(--base-checkbox-pl, 36px);
  height: var(--base-checkbox-height, var(--base-checkbox-size, 24px));
  width: fit-content;

  &__input {
    position: absolute;
    top: 0;
    left: 0;
    margin: 0;
    height: var(--base-checkbox-size, 24px);
    width: var(--base-checkbox-size, 24px);
    opacity: 0;
    cursor: pointer;

    &:hover {
      + #{$this}__label {
        &:before {
          border-color: var(--c-black);
        }
      }
    }

    &:checked {
      + #{$this}__label {
        &:before {
          background-color: var(--c-primary);
          border-color: var(--c-pink-1);
        }
      }
    }

    &:disabled {
      + #{$this}__label {
        &:before {
          background-color: var(--c-light);
          border-color: var(--c-light-1);
        }
      }
    }
  }

  &__icon {
    position: absolute;
    left: var(--checkbox-icon-left, 4px);
    top: var(--checkbox-icon-top, 4px);
    --icon-font-size:  var(--checkbox-icon-size, 16px);

    &:before {
      color: var(--c-white);
    }
  }

  &__label {
    display: block;
    cursor: pointer;
    font-weight: var(--checkbox-label-font-weight, 500);
    font-size: var(--checkbox-label-font-size, 14px);
    line-height: var(--checkbox-label-line-height, 16px);
    color: var(--checkbox-label-color, var(--c-black));

    &:before {
      position: absolute;
      display: var(--base-checkbox-input-display, block);
      content: "";
      top: 0;
      left: 0;
      width: var(--base-checkbox-size, 24px);
      height: var(--base-checkbox-size, 24px);
      background-color: var(--c-white);
      border: var(--base-checkbox-border, 1px solid var(--c-grey-1));
      border-radius: 4px;
    }

    &.required {
      &:after {
        content: "*";
        color: var(--base-checkbox-required-color, var(--c-black));
      }
    }
  }

  &__error-message {
    position: var(--base-checkbox-error-message-position, absolute);
    bottom: var(--base-checkbox-error-message-bottom, -16px);
    left: var(--base-checkbox-error-message-left, 0);
    margin: var(--base-checkbox-error-message-margin, 4px 0 0 36px);
    color: var(--base-checkbox-error-message-color, var(--c-danger));
    font-size: var(--base-checkbox-error-message-font-size, 10px);
    font-weight: var(--base-checkbox-error-message-font-weight, 400);
    line-height: var(--base-checkbox-error-message-line-height, 12px);
  }

  &.invalid {
    --base-checkbox-required-color: var(--c-danger);
  }
}
</style>
